<template>
  <div>
    <ModalBase :show-modal="true" @close-modal="close" :auto-h="true" :narrow="true" :overlay-scrollb="false" class="team-modal">
      <template v-slot:titlebar>
        <TitleBar variant-class="title-modal">
          <template v-slot:title>
            {{ $gettext('Add new team member') }}
          </template>
        </TitleBar>
      </template>
      <template v-slot:maincontent>
        
        <b-form ref="teaminviteform" @submit.prevent="sendInvitation">
          <b-form-row>
            <b-col cols="12" lg="6">
              <b-form-group id="input-group-1" label-class="gk-label-margin gk-modal-label" :label="$gettext('Email*')" label-for="input-1">
                <b-form-input id="input-1" v-model="invitation.invite_to" type="email" required />
              </b-form-group>
            </b-col>
            <b-col cols="12" lg="6">
              <b-form-group id="input-group-2" label-class="gk-label-margin gk-modal-label" :label="$gettext('Name')" label-for="input-2">
                <b-form-input id="input-2" v-model="invitation.invite_to_name" type="text" />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group id="input-group-3" label-class="gk-label-margin gk-modal-label" :label="$gettext('Personal message')" label-for="input-3">
                <b-form-textarea id="input-3" v-model="invitation.message" :placeholder="$gettext('Hi John, I would like to invite you...')" rows="4" />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group id="input-group-4" label-class="gk-label-margin gk-modal-label" :label="$gettext('Company*')" label-for="input-4">
                <b-form-select id="input-4" v-model="invitation.company_profile" value-field="id" text-field="name" :options="availableCompanies" />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group :label="$gettext('User Rights')" label-class="gk-label-margin gk-modal-label" v-slot="{ ariaDescribedby }">
                <b-form-checkbox-group
                  v-model="selectedRights"
                  :options="selectRightsOptions"
                  :aria-describedby="ariaDescribedby"
                  name="user-rights"
                  class="team-modal-user-rights"
                  stacked
                />
              </b-form-group>
            </b-col>
          </b-form-row>
        </b-form>

      </template>

      <template v-slot:actions>
        
        <b-alert :show="(openSlots < 1)" class="gk-alert">
          {{ $gettext('You will be taken to payment after sending this invitation.') }}
        </b-alert>

        <div class="d-flex justify-content-between">
          <GekkoButton 
            @click="close()" 
            :text="$gettext('Cancel')" 
            variant="link"
            class="text-primary px-0"
          />
          <GekkoButton
            @click="sendInvitation()"
            icon="fa-solid fa-paper-plane"
            :text="$gettext('Send invitation')"
            class="position-relative"
            :loading="isWaitingForResponse"
          />
        </div>
      </template>
    </ModalBase>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex"
import GekkoButton from '@/components/GekkoButton'
import ModalBase from "@/components/base_components/ModalBase"
import TitleBar from "@/components/TitleBar"

import UserInviteService from "@/store/services/userinvite.service"
import UserRightsService from "@/store/services/userrights.service"

export default {
  name: 'TeamNewInviteModal',
  components: {
    GekkoButton,
    ModalBase,
    TitleBar
  },
  props: {
    openSlots: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      selectedRights: [],
      invitation: {
        invite_from: null,
        invite_to_name: null,
        invite_to: null,
        message: null,
        rights: {
          hours: true,
          costs: true,
          invoices: true,
          trips: true,
          banks: true,
          edit_company_profile: true,
          change_customers: true,
          financial_details: true,
          access_company_wide_resources: true
        },
        company_profile: null
      }
    }
  },
  computed: {
    ...mapGetters({ 
      availableCompanies: "team/getMyOwnCompanies",
      currentUser: 'main/getCurrentUser',
      isWaitingForResponse: 'main/isWaitingForResponse'
    }),
    selectRightsOptions () {
      return [
        { value: 'invoices',                      text: this.$gettext('Create and send invoices') },
        { value: 'costs',                         text: this.$gettext('Can add and manage costs') },
        { value: 'hours',                         text: this.$gettext('Can track time') },
        { value: 'trips',                         text: this.$gettext('Can log trips') },
        { value: 'banks',                         text: this.$gettext('Can see and manage bank transactions') },
        { value: 'edit_company_profile',          text: this.$gettext('Can manage company (change details, connect accountant, download data)') },
        { value: 'change_customers',              text: this.$gettext('Can see the customers page, create and change your customer/project details') },
        { value: 'financial_details',             text: this.$gettext('Can view financial reports') },
        { value: 'access_company_wide_resources', text: this.$gettext('Can also see information from other users') }
      ]
    }
  },
  created () {
    document.body.classList.add('modal-on')
    this.setDefaultInvitationValues()
  },
  methods: {
    ...mapMutations({
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE'
    }),
    close () {
      this.$emit('close', false)
      document.body.classList.remove('modal-on')
    },
    setDefaultInvitationValues () {
      this.invitation.invite_from = this.currentUser.id
      this.invitation.company_profile = this.currentUser.default_company

      // set default rights (all selected)
      this.selectRightsOptions.forEach(option => {
        this.selectedRights.push(option.value) 
      })
    },
    validateForm () {
      return this.$refs.teaminviteform.reportValidity()
    },
    sendInvitation () {
      console.log('sendInvitation')
      if (this.validateForm()) {
        // set invitation.rights based on this.selectedRights
        for (const key in this.invitation.rights) {
          this.invitation.rights[key] = this.selectedRights.includes(key) ? true : false
        }
        if (this.openSlots > 0) {
          this.setWaitingForResponse(true)
          UserRightsService.create(this.invitation.rights).then((response) => {
            this.invitation.rights = response.data.id
            UserInviteService.create(this.invitation).then(() => {
              this.$store.commit("setAlert", {
                title: this.$gettext("Invitation sent"),
                text: this.$gettext("Invitation has been successfully sent"),
                variant: "success",
                duration: 4
              });
              this.$emit('refresh-data', false)
              this.close()
            }).catch((response) => {
              UserRightsService.destroy(this.invitation.rights)

              const error_title = this.$gettext('Error')
              let error_txt = this.$gettext('An error occurred')

              if (response.status == 403) {
                error_txt = this.$gettext('You do not have access to send this invitation.')
              }
              if (response.status == 400) {
                error_txt = response.data
                if (response.data.invite_to) {
                  error_txt = response.data.invite_to
                }
              } else {
                console.error("The user invite could not be created: " + response.status + "\n" + response.data)
              }

              this.$store.commit('setAlert', { title: error_title, text: error_txt, variant: 'danger', duration: 4 });
            }).finally(() => {
              this.invitation.rights = {}
              this.setWaitingForResponse(false)
            })
          }).catch(() => {
            this.setWaitingForResponse(false)
          })
        } else {
          this.setWaitingForResponse(true)
          UserInviteService.addTeamViaInvite(this.invitation).then((response) => {
            this.$router.push({ name: 'ConfirmPayment', params: { transactionId: response.data } })
          }).catch((response) => {
            const error_title = this.$gettext('Unable to send invite')
            let error_txt = this.$gettext('An error occurred')
            
            if (response.status == 499) {
              error_txt = this.$gettext('In order to perform this action you are required to update to premium.')
              this.$store.commit('togglePremiumModal', 'web_reporting_investments')
            }

            if (response.status == 403) {
              error_txt = response.data['invite_to']
            }

            this.$store.commit('setAlert', { title: error_title, text: error_txt, variant: 'warning', duration: 4 })
            
            this.close()
          }).finally(() => {
            this.setWaitingForResponse(false)
          })
        }
      }
    }
  }
}
</script>

<style lang="scss">
.team-modal {
  .team-modal-user-rights {
    font-size: 14px;
    .custom-control-label {
      padding-top: 2px;
    }
  }
}
</style>

<style lang="scss" scoped>

</style>
